/**
 * Student ID Utility Functions
 * 
 * Handles consistent student ID normalization throughout the application.
 * Removes prefixes like "student_", "IMS", "STU" to get the clean student identifier.
 */

class StudentIdUtils {
  /**
   * Extract clean student IMS PIN from any format
   * @param {string} studentId - Student ID in any format (student_99A77785, IMS99A77785, 99A77785, etc.)
   * @returns {string} Clean IMS PIN (preserves IMS prefix if it's the actual database format)
   */
  static extractCleanPin(studentId) {
    if (!studentId || typeof studentId !== 'string') {
      console.warn('⚠️ StudentIdUtils: Invalid student ID provided:', studentId);
      return '';
    }

    // Remove only the frontend prefix "student_"
    const cleaned = studentId
      .trim()
      .replace(/^student_/i, '');  // Only remove "student_" prefix

    console.log(`🔄 StudentIdUtils: Normalized "${studentId}" → "${cleaned}"`);
    return cleaned;
  }

  /**
   * Add student_ prefix for frontend component compatibility
   * @param {string} cleanPin - Clean IMS PIN (could be IMS0000964290 or UUID format)
   * @returns {string} Prefixed student ID (e.g., "student_IMS0000964290")
   */
  static addStudentPrefix(cleanPin) {
    const clean = this.extractCleanPin(cleanPin);
    return clean ? `student_${clean}` : '';
  }

  /**
   * Validate if a student ID looks valid (basic format check)
   * @param {string} studentId - Student ID to validate
   * @returns {boolean} True if the ID appears valid
   */
  static isValidStudentId(studentId) {
    const cleanPin = this.extractCleanPin(studentId);
    
    // Accept both IMS format (IMS followed by digits) and UUID format
    const imsPattern = /^IMS\d+$/i;
    const uuidPattern = /^[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}$/;
    const generalPattern = /^[A-Za-z0-9]+$/;
    
    return cleanPin.length >= 3 && (imsPattern.test(cleanPin) || uuidPattern.test(cleanPin) || generalPattern.test(cleanPin));
  }

  /**
   * Get the database lookup format (clean PIN without prefixes)
   * @param {string} studentId - Student ID in any format
   * @returns {string} Clean PIN for database queries
   */
  static getDbFormat(studentId) {
    return this.extractCleanPin(studentId);
  }

  /**
   * Get the frontend display format (with student_ prefix)
   * @param {string} studentId - Student ID in any format  
   * @returns {string} Frontend format with student_ prefix
   */
  static getFrontendFormat(studentId) {
    return this.addStudentPrefix(studentId);
  }

  /**
   * Normalize an array of student IDs to consistent format
   * @param {Array} studentIds - Array of student IDs in various formats
   * @param {string} targetFormat - Target format: 'clean', 'frontend', or 'db'
   * @returns {Array} Normalized student IDs
   */
  static normalizeArray(studentIds, targetFormat = 'clean') {
    if (!Array.isArray(studentIds)) {
      console.warn('⚠️ StudentIdUtils: Expected array, got:', typeof studentIds);
      return [];
    }

    return studentIds.map(id => {
      switch (targetFormat) {
        case 'frontend':
          return this.getFrontendFormat(id);
        case 'db':
          return this.getDbFormat(id);
        case 'clean':
        default:
          return this.extractCleanPin(id);
      }
    });
  }
}

export default StudentIdUtils;